import React, { useState, useRef, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { formatDateLocalized } from '../../config/appConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { ChevronDownIcon } from './Icons';

interface DateSelectProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
}

const DateSelect: React.FC<DateSelectProps> = ({
  value = 'all',
  onChange,
  placeholder = 'All Dates',
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { colors } = useTheme();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const dateOptions = [
    { value: 'all', label: 'All Dates', icon: '📅' },
    { value: 'today', label: 'Today', icon: '📍' },
    { value: 'last-week', label: 'Last 7 Days', icon: '📆' },
    { value: 'last-month', label: 'Last Month', icon: '🗓️' },
    { value: 'last-3-months', label: 'Last 3 Months', icon: '📊' },
    { value: 'last-6-months', label: 'Last 6 Months', icon: '📈' },
    { value: 'last-year', label: 'Last Year', icon: '🗓️' },
    { value: 'current-week', label: 'This Week', icon: '📅' },
    { value: 'current-month', label: 'This Month', icon: '🗓️' },
    { value: 'current-year', label: 'This Year', icon: '📆' },
    { value: 'custom', label: 'Custom Range', icon: '⚙️' },
  ];

  const selectedOption = dateOptions.find((option) => option.value === value);
  const displayText = selectedOption ? selectedOption.label : placeholder;

  const handleSelect = (optionValue: string) => {
    onChange?.(optionValue);
    setIsOpen(false);
  };

  // Calculate date range preview for selected option
  const getDateRangePreview = (preset: string): string | null => {
    if (preset === 'all' || preset === 'custom') return null;

    const now = new Date();
    let from: string, to: string;

    switch (preset) {
      case 'today':
        from = to = now.toISOString().split('T')[0];
        break;
      case 'last-week':
        from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        to = now.toISOString().split('T')[0];
        break;
      case 'last-month':
        const lastMonthDate = new Date(now);
        lastMonthDate.setDate(now.getDate() - 30);
        from = lastMonthDate.toISOString().split('T')[0];
        to = now.toISOString().split('T')[0];
        break;
      case 'last-3-months':
        const last3MonthsDate = new Date(now);
        last3MonthsDate.setDate(now.getDate() - 90);
        from = last3MonthsDate.toISOString().split('T')[0];
        to = now.toISOString().split('T')[0];
        break;
      case 'last-6-months':
        const last6MonthsDate = new Date(now);
        last6MonthsDate.setDate(now.getDate() - 180);
        from = last6MonthsDate.toISOString().split('T')[0];
        to = now.toISOString().split('T')[0];
        break;
      case 'last-year':
        const lastYearDate = new Date(now);
        lastYearDate.setDate(now.getDate() - 365);
        from = lastYearDate.toISOString().split('T')[0];
        to = now.toISOString().split('T')[0];
        break;
      case 'current-week':
        const startOfWeek = new Date(now);
        const dayOfWeek = now.getDay();
        const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        startOfWeek.setDate(now.getDate() - daysToMonday);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        from = startOfWeek.toISOString().split('T')[0];
        to = endOfWeek.toISOString().split('T')[0];
        break;
      case 'current-month':
        from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        to = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
        break;
      case 'current-year':
        from = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
        to = new Date(now.getFullYear(), 11, 31).toISOString().split('T')[0];
        break;
      default:
        return null;
    }

    const fromDate = new Date(from);
    const toDate = new Date(to);
    const formatRangeDate = (date: Date) =>
      formatDateLocalized(date, { month: 'short', day: 'numeric' });

    if (from === to) {
      return formatRangeDate(fromDate);
    } else {
      return `${formatRangeDate(fromDate)} - ${formatRangeDate(toDate)}`;
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'w-full flex items-center justify-between px-3 py-2 text-sm border rounded-md transition-all duration-200',
          'focus:outline-none focus:ring-2 focus:ring-offset-0',
          colors.primaryBorderAll ? `focus:${colors.primaryBorderAll.replace('border-', 'ring-')}` : '',
          'hover:shadow-sm backdrop-blur-sm',
          colors.surface,
          colors.text,
          colors.border,
          value !== 'all' && cn(colors.primaryBorderAll, colors.surfaceHover),
          className
        )}
      >
        <div className="flex items-center gap-2 flex-1 text-left">
          {selectedOption && <span className="text-sm">{selectedOption.icon}</span>}
          <div className="flex flex-col min-w-0">
            <span
              className={cn(
                'truncate text-sm font-medium',
                value === 'all' ? colors.textMuted : colors.text
              )}
            >
              {displayText}
            </span>
            {value !== 'all' && value !== 'custom' && (
              <span className={`text-xs ${colors.textMuted} truncate`}>
                {getDateRangePreview(value)}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {value !== 'all' && (
            <div className={cn(colors.primary, colors.primaryText, 'text-xs px-1.5 py-0.5 rounded-full font-medium')}>
              ✓
            </div>
          )}
          <ChevronDownIcon className={cn('w-4 h-4 transition-transform duration-200', isOpen ? 'rotate-180' : '')} />
        </div>
      </button>

      {isOpen && (
        <div
          className={cn(
            'absolute z-50 w-full mt-1 border rounded-md shadow-lg max-h-60 overflow-hidden',
            colors.surface,
            colors.border
          )}
        >
          {/* Header */}
          <div className={`p-3 border-b ${colors.border}`}>
            <div className="flex items-center gap-2">
              <span className={`text-sm font-medium ${colors.textSecondary}`}>📅 Date Range</span>
            </div>
          </div>

          {/* Options */}
          <div className="max-h-48 overflow-y-auto">
            {dateOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors',
                  colors.interactiveHover,
                  value === option.value && cn(colors.surfaceHover, 'border-r-2', colors.primaryBorderAll)
                )}
              >
                <span className="text-sm">{option.icon}</span>
                <div className="flex flex-col min-w-0 flex-1">
                  <span
                    className={cn(
                      'text-sm font-medium',
                      value === option.value ? colors.primaryFg : colors.text
                    )}
                  >
                    {option.label}
                  </span>
                  {option.value !== 'all' && option.value !== 'custom' && (
                    <span className={`text-xs ${colors.textMuted}`}>
                      {getDateRangePreview(option.value)}
                    </span>
                  )}
                </div>
                {value === option.value && (
                  <div className={cn(colors.primary, colors.primaryText, 'text-xs px-1.5 py-0.5 rounded-full font-medium')}>
                    ✓
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

DateSelect.displayName = 'DateSelect';

export { DateSelect };

